import React, { useCallback, useMemo, useState } from 'react';
import { ModalButton, ModalContent, ModalForm } from '../../../../component/fileData/styles';
import { Dropdown, DropdownItem } from '../../../../component/dropdown';
import { InputDiv, SelectedText } from '../styles';
import SVGIcon from '../../../../assets/images/svg/SVGIcon';
import Button from '../../../../component/button';
import {
  MoveFilesInterface,
  ProjectFileData,
  MoveFolderProps,
  DropdownOptionItemProps
} from '../../../../interfaces/ProjectInterface';
import { updateFileData } from '../../../../services/projectServices';
import { captureException } from '../../../../services/logService';
import { isEmpty } from '../../../../helpers/common';
import { rootStore } from '../../../../mobx/rootStore';
import { BUTTON_TYPE, FILE_TYPES, ICON_SIZE } from '../../../../global/constants';
import ModalHeader from '../../../../component/modals/modalHeader';
import Input from '../../../../component/input';

const DropdownOptionItem = React.memo<DropdownOptionItemProps>(({ option, isSelected, onSelectOption }) => {
  const handleOptionClick = useCallback(() => {
    onSelectOption(option);
  }, [onSelectOption, option]);

  return (
    <DropdownItem
      label={option.name}
      onClick={handleOptionClick}
      isSuffix={
        isSelected ? (
          <SVGIcon
            name='checkbox-icon'
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
          />
        ) : null
      }
    />
  );
});

DropdownOptionItem.displayName = 'DropdownOptionItem';

function MoveFolderModal(props: MoveFolderProps) {
  const { onModalCancel, loadData, currentItem } = props;
  // State to store the selected options
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [moveTo, setMoveTo] = useState<MoveFilesInterface | null>(null);
  //mobx store variables
  const { projectStore } = rootStore;
  const { currentProject } = projectStore;

  const onUpdateInputValue = useCallback((obj: MoveFilesInterface) => {
    setInputValue('');
    setMoveTo(obj); // Update your moveTo state if needed
    setIsDropdownOpen(false); // Close the dropdown when an item is selected
  }, []);

  // Toggle dropdown when clicking on InputDiv
  const handleToggleDropdown = useCallback(() => {
    setIsDropdownOpen((prev) => !prev);
  }, []);

  // for move file
  const moveToDirectoryFile = useCallback(
    async (file: ProjectFileData) => {
      try {
        const payload = {
          ParentId: moveTo?.id === 'Files' ? null : moveTo?.id
        };
        const response = await updateFileData(file?.id, payload);
        if (response) {
          loadData();
          onModalCancel();
        }
      } catch (error) {
        captureException(error);
      }
    },
    [loadData, moveTo?.id, onModalCancel]
  );

  // Guarded callback for moving file - prevents calling with null/undefined currentItem
  const handleMoveFileClick = useCallback(() => {
    if (currentItem) {
      moveToDirectoryFile(currentItem);
    }
  }, [currentItem, moveToDirectoryFile]);

  const folderFiles = useMemo(() => {
    const filtered = currentProject?.files.filter((item) => item.uploadFileType === FILE_TYPES.FOLDER);
    return (
      filtered?.map((x) => ({
        id: x.id,
        name: x.name,
        key: x.id,
        value: x.name
      })) || []
    );
  }, [currentProject?.files]);

  return (
    <>
      <ModalHeader title='Select Folder' onClose={onModalCancel} />
      <ModalContent>
        <ModalForm>
          <Dropdown
            isMinWidth={428}
            updateDropdownOpen={setIsDropdownOpen}
            content={
              <InputDiv onClick={handleToggleDropdown}>
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeHolder={isEmpty(moveTo?.name) ? 'Select folder' : ''}
                  trailingIconName='down-arrow-icon'
                  trailingIconClassName={`angleDown-icon ${isDropdownOpen ? 'rotate' : ''}`}
                />
                {isEmpty(inputValue) && moveTo?.name && <SelectedText>{moveTo?.name}</SelectedText>}
              </InputDiv>
            }
            trigger='click'
            onOutsideClick={() => setIsDropdownOpen(false)}>
            {folderFiles
              .filter((option) => option.name && option.name.toLowerCase().includes(inputValue.toLowerCase()))
              .map((option) => {
                const isSelected = !!(moveTo && moveTo.key === option.key);
                return (
                  <DropdownOptionItem
                    key={option.key}
                    option={option}
                    isSelected={isSelected}
                    onSelectOption={onUpdateInputValue}
                  />
                );
              })}
          </Dropdown>
        </ModalForm>
        <ModalButton>
          <Button title='Move' variant={BUTTON_TYPE.PRIMARY} onClick={handleMoveFileClick} />
        </ModalButton>
      </ModalContent>
    </>
  );
}

export default MoveFolderModal;
